import React, { useState, useRef, useEffect } from 'react';
import {
  Building2,
  ChevronDown,
  CircleHelp,
  Eye,
  LogOut,
  Settings,
  ShieldCheck,
  User,
  UserRoundCog,
  Wrench,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL ?? '';

function UserProfile({ user, onLogout }) {
  const [tenant, setTenant] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(user?.mfa_enabled !== false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securityError, setSecurityError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || token === 'demo-token') return;
    fetch(`${API_URL}/auth/tenant`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setTenant(data); })
      .catch(() => {});

    fetch(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setMfaEnabled(data.mfa_enabled !== false);
        const stored = JSON.parse(localStorage.getItem('userData') || '{}');
        localStorage.setItem('userData', JSON.stringify({ ...stored, ...data }));
      })
      .catch(() => {});
  }, [user?.id]);

  const handleMfaToggle = async () => {
    const token = localStorage.getItem('authToken');
    if (!token || token === 'demo-token') return;

    const nextEnabled = !mfaEnabled;
    setSecuritySaving(true);
    setSecurityError('');
    try {
      const response = await fetch(`${API_URL}/auth/me/security/mfa`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Could not update 2FA');
      }
      setMfaEnabled(data.mfa_enabled !== false);
      const stored = JSON.parse(localStorage.getItem('userData') || '{}');
      localStorage.setItem('userData', JSON.stringify({ ...stored, mfa_enabled: data.mfa_enabled !== false }));
    } catch (error) {
      setSecurityError(error.message || 'Could not update 2FA');
    } finally {
      setSecuritySaving(false);
    }
  };
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleIcon = (role) => {
    const icons = {
      admin: ShieldCheck,
      manager: UserRoundCog,
      operator: Wrench,
      viewer: Eye,
    };
    return icons[role] || User;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: '#8B5CF6',    // Purple
      manager: '#3B82F6',  // Blue
      operator: '#10B981', // Green
      viewer: '#6B7280',   // Gray
    };
    return colors[role] || '#6B7280';
  };

  const formatRole = (role) => {
    const labels = {
      admin: 'Admin',
    };
    return labels[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const RoleIcon = getRoleIcon(user.role);

  return (
    <div className="user-profile" ref={dropdownRef}>
      <button
        className="profile-trigger"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-expanded={showDropdown}
        aria-haspopup="true"
      >
        <div className="user-avatar" style={{ backgroundColor: getRoleColor(user.role) }}>
          <RoleIcon size={17} strokeWidth={2.2} />
        </div>
        <div className="user-info">
          <span className="user-name">{user.full_name || user.email}</span>
          <span className="user-role">{tenant ? tenant.name : formatRole(user.role)}</span>
        </div>
        <ChevronDown className={`dropdown-arrow ${showDropdown ? 'open' : ''}`} size={15} strokeWidth={2.2} />
      </button>

      {showDropdown && (
        <div className="profile-dropdown">
          <div className="dropdown-header">
            <div className="user-avatar large" style={{ backgroundColor: getRoleColor(user.role) }}>
              <RoleIcon size={24} strokeWidth={2.1} />
            </div>
            <div className="user-details">
              <h4>{user.full_name || user.email}</h4>
              <p className="user-email">{user.email}</p>
              <span className="role-badge" style={{ backgroundColor: getRoleColor(user.role) }}>
                {formatRole(user.role)}
              </span>
            </div>
          </div>

          {tenant && (
            <div className="profile-tenant">
              <div className="profile-section-label">Organisation</div>
              <div className="profile-tenant-row">
                <div>
                  <div className="profile-tenant-name">{tenant.name}</div>
                  <div className="profile-tenant-meta">
                    <span className={`profile-plan-badge profile-plan-${tenant.plan || 'free'}`}>{tenant.plan}</span>
                    <span className={`profile-status-dot ${tenant.is_active ? 'active' : 'suspended'}`} />
                    <span>{tenant.is_active ? 'Active' : 'Suspended'}</span>
                  </div>
                </div>
                <a href="/organisation" onClick={() => setShowDropdown(false)} className="profile-manage-link">Manage</a>
              </div>
            </div>
          )}

          <div className="dropdown-content">
            {user.departments && user.departments.length > 0 && (
              <div className="user-departments">
                <h5>Your Departments</h5>
                <div className="dept-list">
                  {user.departments.map(dept => (
                    <span key={dept} className="dept-tag">
                      {dept.charAt(0).toUpperCase() + dept.slice(1)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="profile-stats">
              <div className="profile-stat-item">
                <span className="profile-stat-label">Last Login</span>
                <span className="profile-stat-value">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'First time'}
                </span>
              </div>
              <div className="profile-stat-item">
                <span className="profile-stat-label">Member Since</span>
                <span className="profile-stat-value">
                  {new Date(user.created_at || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="profile-security">
              <div>
                <span className="profile-stat-label">Two-factor verification</span>
                <p>{mfaEnabled ? 'Email code required at sign-in' : 'Email code disabled'}</p>
                {securityError && <p className="profile-security-error">{securityError}</p>}
              </div>
              <button
                type="button"
                className={`profile-security-toggle ${mfaEnabled ? 'danger' : 'success'}`}
                onClick={handleMfaToggle}
                disabled={securitySaving}
              >
                {securitySaving ? 'Saving...' : mfaEnabled ? 'Disable 2FA' : 'Enable 2FA'}
              </button>
            </div>
          </div>

          <div className="dropdown-actions">
            <a href="/organisation" className="dropdown-action" style={{ textDecoration: 'none', color: 'inherit' }} onClick={() => setShowDropdown(false)}>
              <Building2 size={16} strokeWidth={2} /> My Organisation
            </a>
            <a href="/settings" className="dropdown-action" style={{ textDecoration: 'none', color: 'inherit' }} onClick={() => setShowDropdown(false)}>
              <Settings size={16} strokeWidth={2} /> Settings
            </a>
            <button className="dropdown-action">
              <CircleHelp size={16} strokeWidth={2} /> Help & Support
            </button>
            <hr className="dropdown-divider" />
            <button className="dropdown-action logout" onClick={onLogout}>
              <LogOut size={16} strokeWidth={2} /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile;
